/**
 * High-Performance Marketing Landing Page with SSG/ISR Support
 * Epic 18: High-Performance Marketing Site with Static Generation at Edge
 *
 * Features:
 * - Static Site Generation (SSG) with ISR
 * - Edge-cached content from Cloudflare KV
 * - Carbon Design System components
 * - WCAG 2.1 AA compliance
 * - <2 second global load time
 * - 90+ PageSpeed score
 */

import React, { useEffect, useState } from 'react';
import {
  Theme,
  Grid,
  Column,
  Layer,
  Button,
  Tile,
  ClickableTile,
  AspectRatio,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Accordion,
  AccordionItem,
  Modal,
  Loading,
  SkeletonText,
  InlineNotification,
  Link,
} from '@carbon/react';
import { ArrowRight, PlayOutline, CheckmarkFilled, Star, StarFilled } from '@carbon/icons-react';
import { Education, Analytics, GlobalPartner, AiGovernanceModel } from '@carbon/pictograms-react';
import { useI18n } from '../../hooks/useI18n';
import { useTheme } from '../../contexts/ThemeContext';
import { PerformanceMonitor } from '../../utils/performance';
import InteractiveDemo from '../../components/marketing/InteractiveDemo';
import TrialSignupWizard from '../../components/marketing/TrialSignupWizard';

// Types matching backend contracts
interface MarketingContent {
  headline: string;
  subheading: string;
  features: Feature[];
  testimonials: Testimonial[];
}

interface Feature {
  id: string;
  icon: string;
  title: string;
  description: string;
  highlight?: string;
}

interface Testimonial {
  id: string;
  quote: string;
  author: string;
  role: string;
  institution: string;
  rating: number;
}

interface StaticProps {
  generatedAt: string;
  ttl: number;
  content: MarketingContent;
  locale: string;
  variant: string;
}

interface HighPerformanceLandingPageProps {
  staticProps?: StaticProps;
  variant?: 'main' | 'institution' | 'parent';
}

const HighPerformanceLandingPage: React.FC<HighPerformanceLandingPageProps> = ({ staticProps, variant = 'main' }) => {
  const [content, setContent] = useState<MarketingContent | null>(staticProps?.content || null);
  const [loading, setLoading] = useState(!staticProps);
  const [showDemo, setShowDemo] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const { t, language } = useI18n();
  const { theme } = useTheme();

  // Track performance metrics
  useEffect(() => {
    const startTime = performance.now();
    // Track performance metric - landing page load

    // Report Core Web Vitals
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'largest-contentful-paint') {
            // Track LCP metric
          } else if (entry.entryType === 'first-input') {
            // Track FID metric
          } else if (entry.entryType === 'layout-shift') {
            // Track CLS metric
          }
        }
      });
      observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'] });
      return () => observer.disconnect();
    }
  }, []);

  // Fetch content if not provided via SSG
  useEffect(() => {
    if (!staticProps && !content) {
      fetchContent();
    }
  }, [staticProps, content, variant]);

  const fetchContent = async () => {
    try {
      // Use the correct API URL based on environment
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/v1/marketing/content/${variant}`, {
        headers: {
          'Accept-Language': language || 'en',
          'X-Visitor-ID': getVisitorId(),
        },
      });

      if (!response.ok) {
        console.error('Content fetch failed:', response.status, response.statusText);
        // Use fallback content
        setContent(getFallbackContent(variant));
        setLoading(false);
        return;
      }

      const data = await response.json();
      setContent(data.content || getFallbackContent(variant));
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      // Use fallback content on error
      setContent(getFallbackContent(variant));
      setLoading(false);
    }
  };

  const getFallbackContent = (variantType: string): MarketingContent => {
    const baseContent = {
      headline: 'Transform Education with AI-Powered Learning',
      subheading: "Personalized tutoring that adapts to every student's unique learning style",
      features: [
        {
          id: 'f1',
          icon: 'Education',
          title: 'Personalized Learning',
          description: "AI adapts to each student's learning style and pace",
          highlight: 'Adaptive AI Technology',
        },
        {
          id: 'f2',
          icon: 'Analytics',
          title: 'Real-time Progress Tracking',
          description: 'Monitor student progress with detailed analytics',
          highlight: 'Data-Driven Insights',
        },
        {
          id: 'f3',
          icon: 'GlobalPartner',
          title: 'Curriculum Aligned',
          description: 'Content aligned with educational standards',
          highlight: 'Standards Compliant',
        },
      ],
      testimonials: [
        {
          id: 't1',
          quote: 'Sigma08 has transformed how our students learn.',
          author: 'Sarah Johnson',
          role: 'Principal',
          institution: 'Lincoln High School',
          rating: 5,
        },
      ],
    };

    // Customize content based on variant
    if (variantType === 'institution') {
      baseContent.headline = 'Empower Your Institution with AI-Driven Education';
      baseContent.subheading = 'Transform learning outcomes with personalized AI tutoring at scale';
    } else if (variantType === 'parent') {
      baseContent.headline = 'Give Your Child the Educational Edge';
      baseContent.subheading = 'Personalized AI tutoring that helps your child excel';
    }

    return baseContent;
  };

  const getVisitorId = () => {
    let visitorId = localStorage.getItem('visitorId');
    if (!visitorId) {
      visitorId = `v-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('visitorId', visitorId);
    }
    return visitorId;
  };

  const getIconComponent = (iconName: string) => {
    const icons: { [key: string]: React.ReactNode } = {
      Education: <Education size={48} />,
      Analytics: <Analytics size={48} />,
      GlobalPartner: <GlobalPartner size={48} />,
      AIGovernance: <AiGovernanceModel size={48} />,
    };
    return icons[iconName] || <Education size={48} />;
  };

  if (loading) {
    return (
      <Grid className="landing-page--skeleton">
        <Column lg={16} md={8} sm={4}>
          <SkeletonText heading className="landing-page--skeleton-hero" />
          <SkeletonText paragraph lineCount={4} />
        </Column>
      </Grid>
    );
  }

  if (!content) {
    return (
      <Grid>
        <Column lg={16} md={8} sm={4}>
          <InlineNotification kind="error" title="Content unavailable" subtitle="Please try refreshing the page" />
        </Column>
      </Grid>
    );
  }

  return (
    <div>
      {/* Skip Navigation Links for Accessibility */}
      <a href="#main-content" className="skip-navigation">
        {t('navigation:accessibility.skipToContent')}
      </a>
      <a href="#features" className="skip-navigation">
        {t('navigation:accessibility.skipToFeatures', 'Skip to features')}
      </a>

      {/* Hero Section */}
      <section id="main-content" className="hero-section" role="banner">
        <Grid>
          <Column lg={{ span: 10, offset: 3 }} md={8} sm={4}>
            <Layer>
              <h1 className="hero-headline">{content.headline}</h1>
              <p className="hero-subheading">{content.subheading}</p>
              <div className="hero-actions">
                <Button kind="primary" size="lg" renderIcon={ArrowRight} onClick={() => setShowSignup(true)}>
                  {t('marketing:cta.button', 'Start Free Trial')}
                </Button>
                <Button kind="ghost" size="lg" renderIcon={PlayOutline} onClick={() => setShowDemo(true)}>
                  {t('marketing:cta.watchDemo', 'Watch Demo')}
                </Button>
              </div>
            </Layer>
          </Column>
        </Grid>
      </section>

      {/* Features Section with Smooth Scroll */}
      <section id="features" className="features-section" role="region" aria-label="Features">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <h2 className="section-title">{t('marketing:features.title')}</h2>
            <Tabs selectedIndex={activeFeature} onChange={({ selectedIndex }) => setActiveFeature(selectedIndex)}>
              <TabList aria-label="Feature categories">
                {content.features.map((feature, index) => (
                  <Tab key={feature.id}>{feature.title}</Tab>
                ))}
              </TabList>
              <TabPanels>
                {content.features.map(feature => (
                  <TabPanel key={feature.id}>
                    <Grid>
                      <Column lg={4} md={8} sm={4}>
                        <div className="feature-icon">{getIconComponent(feature.icon)}</div>
                      </Column>
                      <Column lg={12} md={8} sm={4}>
                        <h3>{feature.title}</h3>
                        <p>{feature.description}</p>
                        {feature.highlight && (
                          <div className="feature-highlight">
                            <CheckmarkFilled />
                            <span>{feature.highlight}</span>
                          </div>
                        )}
                      </Column>
                    </Grid>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </Column>
        </Grid>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section" role="region" aria-label="Testimonials">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <h2 className="section-title">{t('marketing:testimonials.title')}</h2>
            <Accordion>
              {content.testimonials.map(testimonial => (
                <AccordionItem key={testimonial.id} title={`${testimonial.author} - ${testimonial.role}`}>
                  <blockquote className="testimonial-quote">"{testimonial.quote}"</blockquote>
                  <div className="testimonial-author">
                    <strong>{testimonial.author}</strong>
                    <span>
                      {testimonial.role} at {testimonial.institution}
                    </span>
                    <div className="testimonial-rating" aria-label={`Rating: ${testimonial.rating} out of 5`}>
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className={i < testimonial.rating ? 'star-filled' : 'star-empty'}>
                          {i < testimonial.rating ? <StarFilled size={16} /> : <Star size={16} />}
                        </span>
                      ))}
                    </div>
                  </div>
                </AccordionItem>
              ))}
            </Accordion>
          </Column>
        </Grid>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" role="region" aria-label="Pricing">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <h2 className="section-title">{t('marketing:pricing.title')}</h2>
            <Grid narrow>
              <Column lg={5} md={8} sm={4}>
                <ClickableTile href="/pricing/starter">
                  <h3>{t('marketing:pricing.plans.starter.name')}</h3>
                  <p className="pricing-value">$29/month</p>
                  <ul>
                    <li>
                      <CheckmarkFilled size={16} /> Up to 30 students
                    </li>
                    <li>
                      <CheckmarkFilled size={16} /> Basic AI features
                    </li>
                    <li>
                      <CheckmarkFilled size={16} /> Email support
                    </li>
                  </ul>
                </ClickableTile>
              </Column>
              <Column lg={5} md={8} sm={4}>
                <ClickableTile href="/pricing/professional" className="recommended">
                  <h3>{t('marketing:pricing.plans.professional.name')}</h3>
                  <p className="pricing-value">$99/month</p>
                  <ul>
                    <li>
                      <CheckmarkFilled size={16} /> Up to 200 students
                    </li>
                    <li>
                      <CheckmarkFilled size={16} /> Advanced AI features
                    </li>
                    <li>
                      <CheckmarkFilled size={16} /> Priority support
                    </li>
                    <li>
                      <CheckmarkFilled size={16} /> Analytics dashboard
                    </li>
                  </ul>
                </ClickableTile>
              </Column>
              <Column lg={5} md={8} sm={4}>
                <ClickableTile href="/pricing/enterprise">
                  <h3>{t('pricing.enterprise.title')}</h3>
                  <p className="pricing-value">Custom</p>
                  <ul>
                    <li>
                      <CheckmarkFilled size={16} /> Unlimited students
                    </li>
                    <li>
                      <CheckmarkFilled size={16} /> Custom AI models
                    </li>
                    <li>
                      <CheckmarkFilled size={16} /> Dedicated support
                    </li>
                    <li>
                      <CheckmarkFilled size={16} /> API access
                    </li>
                  </ul>
                </ClickableTile>
              </Column>
            </Grid>
          </Column>
        </Grid>
      </section>

      {/* CTA Section */}
      <section className="cta-section" role="region" aria-label="Call to action">
        <Grid>
          <Column lg={{ span: 10, offset: 3 }} md={8} sm={4}>
            <Layer>
              <h2>{t('cta.finalTitle')}</h2>
              <p>{t('cta.finalSubtitle')}</p>
              <Button kind="primary" size="lg" renderIcon={ArrowRight} onClick={() => setShowSignup(true)}>
                {t('cta.getStartedNow')}
              </Button>
            </Layer>
          </Column>
        </Grid>
      </section>

      {/* Interactive Demo Modal */}
      <Modal
        open={showDemo}
        onRequestClose={() => setShowDemo(false)}
        modalHeading={t('demo.title')}
        primaryButtonText={t('demo.startTrial')}
        secondaryButtonText={t('demo.close')}
        onRequestSubmit={() => {
          setShowDemo(false);
          setShowSignup(true);
        }}
        size="lg"
      >
        <InteractiveDemo variant={variant} />
      </Modal>

      {/* Trial Signup Modal */}
      <Modal
        open={showSignup}
        onRequestClose={() => setShowSignup(false)}
        modalHeading={t('signup.title')}
        passiveModal
        size="md"
      >
        <TrialSignupWizard onComplete={() => setShowSignup(false)} source={`landing-${variant}`} />
      </Modal>
    </div>
  );
};

// Export with SSG support
export default HighPerformanceLandingPage;

// Static generation function for Next.js/Remix compatibility
export async function getStaticProps(context: any) {
  const variant = context.params?.variant || 'main';
  const locale = context.locale || 'en';

  try {
    const response = await fetch(
      `${import.meta.env.VITE_WORKER_URL || window.location.origin}/api/marketing/static-props`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variant, locale }),
      }
    );

    const data = await response.json();

    return {
      props: {
        staticProps: data,
      },
      revalidate: 3600, // ISR: Regenerate every hour
    };
  } catch (error) {
    console.error('SSG failed:', error);
    return {
      props: {
        staticProps: null,
      },
      revalidate: 60, // Retry sooner on error
    };
  }
}
